import { useForm, ValidationError } from '@formspree/react';
import { RiCloseLine, RiCheckboxCircleFill } from '@remixicon/react';
import { cn } from '../../lib/utils';

const FORM_ID = 'mrewnjap';

const PROFILS = ['Voyageur', 'Transporteur', 'Presse', 'Développeur', 'Autre'];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ContactModal({ open, onClose }: Props) {
  const [state, handleSubmit] = useForm(FORM_ID);

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="w-full sm:max-w-[480px] bg-white sm:rounded-2xl shadow-2xl overflow-hidden
          max-h-[95dvh] flex flex-col rounded-t-2xl">

          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-surface-ui shrink-0">
            <div>
              <p className="font-display font-bold text-[18px] text-ink">Contactez-nous</p>
              <p className="font-display text-[13px] text-ink-faint mt-0.5">Réponse sous 48 h</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-ink-faint hover:bg-surface-ui"
              aria-label="Fermer"
            >
              <RiCloseLine className="h-5 w-5" />
            </button>
          </div>

          {/* Contenu */}
          <div className="overflow-y-auto flex-1 px-6 py-5">
            {state.succeeded ? (
              <div className="flex flex-col items-center gap-4 py-10 text-center">
                <RiCheckboxCircleFill className="h-12 w-12 text-brand" />
                <p className="font-display font-semibold text-[17px] text-ink">Message envoyé !</p>
                <p className="font-display text-[14px] text-ink-muted">On te répondra dans les 48 h.</p>
                <button
                  onClick={onClose}
                  className="mt-2 h-11 px-8 rounded-xl bg-brand text-white font-display font-semibold text-[14px]"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">

                {/* Nom / Prénom */}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Nom" name="nom" required state={state} />
                  <Field label="Prénom" name="prenom" required state={state} />
                </div>

                {/* Email */}
                <Field label="Email" name="email" type="email" required state={state} />

                {/* Profil */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="profil" className="font-display text-[13px] font-semibold text-ink-secondary">
                    Profil <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="profil"
                      name="profil"
                      required
                      className="w-full h-11 rounded-xl border border-line-light bg-white ps-3.5 pe-9
                        font-display text-[14px] text-ink appearance-none
                        focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                    >
                      <option value="">Choisir…</option>
                      {PROFILS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    <svg className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-faint pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <ValidationError field="profil" prefix="Profil" errors={state.errors}
                    className="text-[12px] font-display text-danger" />
                </div>

                {/* Objet */}
                <Field label="Objet" name="objet" required state={state} />

                {/* Message */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="message" className="font-display text-[13px] font-semibold text-ink-secondary">
                    Message <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    placeholder="Ton message…"
                    className="w-full rounded-xl border border-line-light bg-white px-3.5 py-3
                      font-display text-[14px] text-ink resize-none placeholder:text-ink-faint
                      focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                  />
                  <ValidationError field="message" prefix="Message" errors={state.errors}
                    className="text-[12px] font-display text-danger" />
                </div>

                {/* Erreur globale */}
                <ValidationError errors={state.errors}
                  className="text-[12px] font-display text-danger -mt-1" />

                <button
                  type="submit"
                  disabled={state.submitting}
                  className={cn(
                    'h-12 rounded-xl font-display font-semibold text-[15px] text-white transition-opacity mt-1',
                    state.submitting ? 'bg-brand/60 cursor-not-allowed' : 'bg-brand',
                  )}
                >
                  {state.submitting ? 'Envoi…' : 'Envoyer le message'}
                </button>

              </form>
            )}
          </div>

        </div>
      </div>
    </>
  );
}

function Field({
  label, name, type = 'text', required = false, state,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  state: ReturnType<typeof useForm>[0];
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={name} className="font-display text-[13px] font-semibold text-ink-secondary">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <input
        id={name}
        type={type}
        name={name}
        required={required}
        className="h-11 rounded-xl border border-line-light bg-white px-3.5
          font-display text-[14px] text-ink placeholder:text-ink-faint
          focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
      />
      <ValidationError field={name} prefix={label} errors={state.errors}
        className="text-[12px] font-display text-danger" />
    </div>
  );
}
